import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { MonthKey } from '../../domain/analytics';
import type { Anomaly } from '../../domain/anomaly';
import type { Category, Subcategory } from '../../domain/model';
import { formatDayMonth, formatMonthYear } from '../../i18n/format';
import { categoryLabel } from '../categories/labels';
import { Amount, BottomSheet, Button, CategoryDot, List, ListRow, Section, series, useFormat } from '../../ui';
import './anomalies.css';

/**
 * What is out of the ordinary in a month (V1.5), as a short list. A row
 * opens a sheet that says why, with the way to the entries and — on Home —
 * "not unusual" to hide it on this device.
 */
export function AnomalyList({
  anomalies,
  month,
  current,
  categories,
  subcategories,
  title,
  limit,
  onDismiss,
}: {
  anomalies: readonly Anomaly[];
  month: MonthKey;
  /** The month is in progress: its amounts are "so far". */
  current: boolean;
  categories: ReadonlyMap<string, Category>;
  subcategories: ReadonlyMap<string, Subcategory>;
  title: string;
  /** Rows shown before "Show all". */
  limit?: number;
  onDismiss?: (key: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { money, percent, formatLocale } = useFormat();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState<Anomaly | null>(null);
  if (anomalies.length === 0) return null;

  const whole = (minor: number) => money(minor, { whole: true });
  const times = (ratio: number) => new Intl.NumberFormat(formatLocale, { maximumFractionDigits: 1 }).format(ratio);
  const day = (date: string) => formatDayMonth(date, i18n.language);
  const monthName = formatMonthYear(Number(month.slice(0, 4)), Number(month.slice(5, 7)), i18n.language);
  const label = (a: Anomaly) =>
    a.kind === 'bill' && a.template
      ? a.template.name
      : a.subcategoryId
        ? categoryLabel(t, subcategories.get(a.subcategoryId))
        : categoryLabel(t, categories.get(a.categoryId));

  const subtitle = (a: Anomaly): string => {
    switch (a.kind) {
      case 'category':
        return a.isNew ? t('anomaly.row.new', { count: a.months }) : t('anomaly.row.category', { times: times(a.amount / a.usual), usual: whole(a.usual) });
      case 'entry':
        return t('anomaly.row.entry', { date: day(a.entry.date), times: times(a.amount / a.usual), usual: whole(a.usual) });
      case 'bill':
        return t('anomaly.row.bill', { percent: percent(a.amount / a.usual - 1, { signed: false, digits: 0 }), usual: whole(a.usual) });
      case 'duplicate':
        return t('anomaly.row.duplicate', { date: day(a.entry.date) });
    }
  };

  const body = (a: Anomaly): string => {
    switch (a.kind) {
      case 'category':
        return a.isNew
          ? t('anomaly.sheet.new', { amount: whole(a.amount), month: monthName, count: a.months })
          : t(current ? 'anomaly.sheet.categorySoFar' : 'anomaly.sheet.category', {
              amount: whole(a.amount),
              month: monthName,
              usual: whole(a.usual),
              count: a.months,
            });
      case 'entry':
        return t('anomaly.sheet.entry', { amount: whole(a.amount), date: day(a.entry.date), usual: whole(a.usual), count: a.sample });
      case 'bill':
        return t('anomaly.sheet.bill', { name: label(a), amount: whole(a.amount), usual: whole(a.usual) });
      case 'duplicate':
        return t('anomaly.sheet.duplicate', { amount: whole(a.amount), count: Math.max(1, Math.round((a.entry.createdAt - a.first.createdAt) / 1000)) });
    }
  };

  const show = () => {
    const a = open!;
    setOpen(null);
    if (a.kind === 'category') {
      const days = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
      const q = new URLSearchParams({ c: a.categoryId, ...(a.subcategoryId ? { s: a.subcategoryId } : {}), from: `${month}-01`, to: `${month}-${days}` });
      navigate(`/entries?${q}`);
    } else {
      navigate(`/entry/${a.entry.id}`, { state: { background: location } });
    }
  };

  const shown = limit && !expanded ? anomalies.slice(0, limit) : anomalies;
  return (
    <Section
      title={title}
      className="anomalies"
      data-testid="anomalies"
      action={
        limit && anomalies.length > limit ? (
          <button type="button" className="link-button" onClick={() => setExpanded((x) => !x)} aria-expanded={expanded}>
            {expanded ? t('anomaly.showFewer') : t('anomaly.showAll', { count: anomalies.length })}
          </button>
        ) : undefined
      }
    >
      <List label={title}>
        {shown.map((a) => (
          <ListRow
            key={a.key}
            density="compact"
            leading={<CategoryDot color={series(categories.get(a.categoryId)?.colorSlot ?? 0)} />}
            title={label(a)}
            subtitle={subtitle(a)}
            trailing={<Amount minor={a.amount} whole />}
            onClick={() => setOpen(a)}
          />
        ))}
      </List>

      <BottomSheet
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? label(open) : ''}
        footer={
          open && (
            <>
              <Button variant="primary" size="lg" block onClick={show}>
                {open.kind === 'category' ? t('anomaly.seeEntries') : t('anomaly.openEntry')}
              </Button>
              {onDismiss && (
                <Button
                  variant="secondary"
                  size="lg"
                  block
                  onClick={() => {
                    onDismiss(open.key);
                    setOpen(null);
                  }}
                >
                  {t('anomaly.dismiss')}
                </Button>
              )}
            </>
          )
        }
      >
        {open && <p className="anomaly-body">{body(open)}</p>}
      </BottomSheet>
    </Section>
  );
}
